// src/hooks/usePost.js
import { useState } from 'react';
import { readJson, describeHttpFailure } from './readJson';
import { base_url } from './apiConfig';

export const useAuth = (url, initialData = null) => {
  const [ApiData, setData] = useState(initialData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const toggleLoader = (show) => {
    const loaderBox = document.querySelector('.loaderBox');
    if (loaderBox) {
      loaderBox.classList.toggle('d-none', !show);
    }
  };

  const post = async (postData) => {
    setLoading(true);
    setError(null);
    toggleLoader(true);

    try {
      const response = await fetch(base_url + url, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(postData),
      });

      const result = await readJson(response);
      
      if (!response.ok) {
        // Set the API response as error data for better error handling
        setError(result);
        setData(null);
      } else {
        setData(result);
        setError(null);
      }
    } catch (err) {
      setError({
        message: err?.friendly ? err.message : 'Network error. Please check your connection.',
        success: false
      });
      setData(null);
    } finally {
      setLoading(false);
      toggleLoader(false);
    }
  };

  return { ApiData, loading, error, post };
};


export const usePost = (url, initialData = null) => {
  const [ApiData, setData] = useState(initialData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const LogoutData = localStorage.getItem('login');

  const toggleLoader = (show) => {
    const loaderBox = document.querySelector('.loaderBox');
    if (loaderBox) {
      loaderBox.classList.toggle('d-none', !show);
    }
  };

  const post = async (postData) => {
    // Ensure 'status' is a boolean
    if (postData.hasOwnProperty('status')) {
      postData.status = postData.status === 'true' || postData.status === true;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(base_url + url, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${LogoutData}`,
        },
        body: JSON.stringify(postData), // Convert object to JSON string
      });

      if (!response.ok) {
        const result = await readJson(response);
        setError(result || { message: 'Network response was not ok' });
        setData(null);
        setLoading(false);
        return;
      }

      const result = await readJson(response);
      setData(result);
      setError(null);
      setLoading(false);
    } catch (err) {
      setError(err);
      setLoading(false);
    }
  };


  return { ApiData, loading, error, post };
};

export const usePostForm = (url, initialData = null) => {
  const [ApiData, setData] = useState(initialData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const LogoutData = localStorage.getItem('login');

  const toggleLoader = (show) => {
    const loaderBox = document.querySelector('.loaderBox');
    if (loaderBox) {
      loaderBox.classList.toggle('d-none', !show);
    }
  };

  const post = async (postData) => {
    setLoading(true);
    setError(null);
    toggleLoader(true); // Show the loader

    try {
      const formData = new FormData();

      // Convert `postData` object into FormData format
      Object.keys(postData).forEach((key) => {
        formData.append(key, postData[key]);
      });

      const response = await fetch(base_url + url, {
        method: 'POST',
        headers: {
          Accept: 'application/json', // otherwise Laravel answers errors with an HTML page
          Authorization: `Bearer ${LogoutData}`, // Authorization remains in headers
        },
        body: formData, // Send FormData directly
      });

      const result = await response.json().catch(() => null);

      if (!response.ok) {
        // Keep the API payload so screens can display validation errors (422, etc.)
        setError(result || { message: 'Request failed', success: false });
        setData(null);
        return;
      }

      setData(result);
    } catch (err) {
      setError({
        message: err?.friendly ? err.message : 'Network error. Please check your connection.',
        success: false
      });
      setData(null);
    } finally {
      setLoading(false);
      toggleLoader(false); // Hide the loader
    }
  };

  return { ApiData, loading, error, post };
};



export const usePatch = (url, initialData = null) => {
  const [ApiData, setData] = useState(initialData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const LogoutData = localStorage.getItem('login');

  const toggleLoader = (show) => {
    const loaderBox = document.querySelector('.loaderBox');
    if (loaderBox) {
      loaderBox.classList.toggle('d-none', !show);
    }
  };

  const patch = async (postData) => {
    // Ensure 'status' is a boolean
    if (postData.hasOwnProperty('status')) {
      postData.status = postData.status === 'true' || postData.status === true;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(base_url + url, {
        method: 'PATCH',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${LogoutData}`,
        },
        body: JSON.stringify(postData), // Convert object to JSON string
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const result = await readJson(response);
      setData(result);
      setLoading(false);
    } catch (err) {
      setError(err);
      setLoading(false);
    }
  };


  return { ApiData, loading, error, patch };
};

export const usePut = (url, initialData = null) => {
  const [ApiData, setData] = useState(initialData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const LogoutData = localStorage.getItem('login');

  const toggleLoader = (show) => {
    const loaderBox = document.querySelector('.loaderBox');
    if (loaderBox) {
      loaderBox.classList.toggle('d-none', !show);
    }
  };

  const put = async (putData) => {
    // Ensure 'status' is a boolean
    if (putData.hasOwnProperty('status')) {
      putData.status = putData.status === 'true' || putData.status === true;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(base_url + url, {
        method: 'PUT',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${LogoutData}`,
        },
        body: JSON.stringify(putData), // Convert object to JSON string
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const result = await readJson(response);
      setData(result);
      setLoading(false);
    } catch (err) {
      setError(err);
      setLoading(false);
    }
  };

  return { ApiData, loading, error, put };
};


export const usePatchForm = (url, initialData = null) => {
  const [ApiData, setData] = useState(initialData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const LogoutData = localStorage.getItem('login');

  const toggleLoader = (show) => {
    const loaderBox = document.querySelector('.loaderBox');
    if (loaderBox) {
      loaderBox.classList.toggle('d-none', !show);
    }
  };

  const patch = async (patchData) => {
    setLoading(true);
    setError(null);
    toggleLoader(true); // Show the loader

    try {
      const formData = new FormData();

      // Convert `patchData` object into FormData format
      Object.keys(patchData).forEach((key) => {
        formData.append(key, patchData[key]);
      });

      const response = await fetch(base_url + url, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${LogoutData}`, // Authorization remains in headers
        },
        body: formData, // Send FormData directly
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const result = await readJson(response);
      setData(result);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
      toggleLoader(false); // Hide the loader
    }
  };

  return { ApiData, loading, error, patch };
};






export const useGet = (url, initialData = null, idData = '') => {
  const [ApiData, setData] = useState(initialData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const LogoutData = localStorage.getItem('login');
  const [isTriggered, setIsTriggered] = useState(false);

  const toggleLoader = (show) => {
    const loaderBox = document.querySelector('.loaderBox');
    if (loaderBox) {
      loaderBox.classList.toggle('d-none', !show);
    }
  };

  const get = async (overrideUrl = null, skipLoader = false) => {
    setLoading(true);
    setError(null);
    if (!skipLoader) toggleLoader(true);

    try {
      const finalUrl = overrideUrl
        ? base_url + overrideUrl
        : base_url + url + idData;
      const response = await fetch(finalUrl, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${LogoutData}`,
        },
      });
      if (!response.ok) {
        let errorData = null;
        try {
          errorData = await readJson(response);
        } catch (e) {
          errorData = null;
        }
        const error = new Error(
          errorData?.message || errorData?.error || describeHttpFailure(response.status)
        );
        error.status = response.status;
        error.data = errorData;
        throw error;
      }
      const result = await readJson(response);
      setData(result);
      setError(null);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
      if (!skipLoader) toggleLoader(false);
    }
  };

  return { ApiData, loading, error, get, setData };
};



export const useDelete = (url, initialData = null, idData = '') => {
  const [ApiData, setData] = useState(initialData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const LogoutData = localStorage.getItem('login');
  const [isTriggered, setIsTriggered] = useState(false);

  const toggleLoader = (show) => {
    const loaderBox = document.querySelector('.loaderBox');
    if (loaderBox) {
      loaderBox.classList.toggle('d-none', !show);
    }
  };

  const del = async () => {
    setLoading(true);
    setError(null);
    toggleLoader(true); // Show the loader

    try {
      const response = await fetch(base_url + url + idData, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${LogoutData}`,
        },
      });
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const result = await readJson(response);
      setData(result);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
      toggleLoader(false); // Hide the loader
    }
  };

  return { ApiData, loading, error, del };
};
